/**
 * Retail & Tea Room Sales — daily EPOS takings by outlet/location.
 *
 * Reads /api/dashboards/sales/epos?fiscalYear=… (one NYMR season of daily rows:
 * date × category × location × class × gross/net/vat/items/transactions) and
 * renders it as two matrices — Retail and Tea Rooms — locations across, time
 * down. Pick a month → daily rows; "Whole season" → monthly rows. A season
 * summary (retail by location) + Shop-sales budget sits on top.
 *
 * Extras for the retail team:
 *  - Round up pill: whole-£ vs £x.xx pence.
 *  - Breakdown pill: click any figure → popup splitting it by MERAC sales
 *    element (Branded Souvenirs, Gift Cards, Retail Donations, Hot Drinks, …).
 *  - Tea Rooms table has Catering / Retail class pills (both on) so the small
 *    retail-at-tea-rooms slice is visible.
 *  - Budget overlay: Shop-sales (and Catering) monthly budget by the table
 *    total with % variance, plus this-month / to-date / full-year budget tiles.
 *
 * NYMR season runs 1 Mar – end Feb, labelled by the end year ("2026/27").
 * Headline figure is Net (ex-VAT); a Net/Gross toggle switches every figure.
 * Budget is a net figure, so budget variance always compares to Net actuals.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import writeXlsxFile from "write-excel-file/browser";
import "./sales.css";
import { ProductFilters } from "./sales/ProductFilters";
import { ThankYouVoucherBox, downloadTyvPdf } from "./sales/ThankYouVoucherBox";
import { GuideBookBox } from "./sales/GuideBookBox";
import { GiftCardOverviewModal } from "./sales/GiftCardOverviewModal";
import type { AnalysesResponse, GuideBooksResponse, Segment, TyvResponse } from "./sales/salesInsightsTypes";
import { CompareToggle, LyCell, LY_GREY, lyWindow, type CompareAlign, type CompareWindows } from "./compare";

type Measure = "Net" | "Gross";
// Basis (2026-10-23, Kathryn Watson plan §1.3) — the Default / Kathryn's Variant 1 dropdown.
//   saletype  Default: PBI.AllSales by SaleType (Shop / Tea Room) — merchandise takings; the
//             walk-up tickets and car parking rung through the shop tills are left out. Net headline.
//   dept      Kathryn's Variant 1: everything the Retail Shops, Whitby Booking Office & Retail and
//             Tea Rooms departments rang through their tills, whatever the sale type — the way the
//             FY2025 AZEPOS Power BI table counts it. Gross headline. Rows carry WalkUpGross/WalkUpNet.
type Basis = "saletype" | "dept";
const BASIS_LABEL: Record<Basis, string> = { saletype: "Default", dept: "Kathryn's Variant 1" };
const BASIS_NOTE: Record<Basis, string> = {
  saletype: "Default counts shop and tea-room sales by sale type — merchandise only; walk-up tickets and car parking rung through the shop tills are left out (headline Net).",
  dept: "Kathryn's Variant 1 counts everything the Retail Shops, Whitby Booking Office & Retail and Tea Rooms departments rang through their tills — including walk-up tickets and car parking — as the FY2025 AZEPOS Power BI table does (headline Gross).",
};
const CATEGORIES = ["Retail", "Tea Room"] as const;
type Category = (typeof CATEGORIES)[number];
const CLASSES = ["Catering", "Retail"] as const;
type SaleClass = (typeof CLASSES)[number];

interface SalesRow {
  SaleDate: string; // YYYY-MM-DD
  Category: Category;
  LocationCode: string | null;
  Location: string;
  Class: SaleClass;
  Gross: number;
  Net: number;
  VAT: number;
  Items: number;
  Transactions: number;
  WalkUpGross?: number; // dept basis only — ticket / car-park / booking lines through this till
  WalkUpNet?: number;
}

interface SalesResponse {
  fiscalYear: number;
  fiscalYearLabel: string;
  years: { fiscalYear: number; label: string }[];
  last_refresh: string | null;
  basis?: Basis;
  rows: SalesRow[];
}
// ?compare=ly → both windows computed by the same server code (plan §1.5).
interface CompareResponse { current: SalesResponse; last_season: SalesResponse; windows: CompareWindows }

/** Move a last-season date forward onto this season's calendar so the same bucketing applies
 *  (inverse of compare.tsx lyDate): "Same weeks" = +364 days; "Same dates" = +1 year. */
function shiftForward(iso: string, align: CompareAlign): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (align === "week") return new Date(d.getTime() + 364 * 86_400_000).toISOString().slice(0, 10);
  const y = d.getUTCFullYear() + 1, m = d.getUTCMonth(), day = d.getUTCDate();
  const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return new Date(Date.UTC(y, m, Math.min(day, last))).toISOString().slice(0, 10);
}

interface BudgetLine {
  months: number[]; // index 0 = Mar (fiscal month 1) .. 11 = Feb
  total: number;
}
interface BudgetResponse {
  fiscalYear: number;
  season: string;
  retail: BudgetLine;
  teaRoom: BudgetLine;
  has_budget: boolean;
}

interface BreakdownItem {
  Analysis: string | null;
  Gross: number;
  Net: number;
  Items: number;
  WalkUp?: boolean; // dept basis: a ticket / car-park / booking element through the shop till
}

// Movers & Shakers (R1) — top retail products by revenue, with cost + margin.
interface MoverRow {
  plu: string;
  item: string;
  analysis: string | null;
  qty: number;
  gross: number;
  net: number;
  cost: number;
  margin: number;
  margin_pct: number | null;
  has_cost: boolean;
}
interface MoversResp {
  from: string;
  to: string;
  coverage_pct: number | null;
  total_net: number;
  rows: MoverRow[];
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const gbp0 = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 });
const gbp2 = new Intl.NumberFormat("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
function money0(n: number): string {
  return `£${gbp0.format(Math.round(n))}`;
}
/** Whole-pound view when roundUp is on; full £x.xx pence when off. */
function money(n: number, roundUp: boolean): string {
  return roundUp ? money0(n) : `£${gbp2.format(n)}`;
}

// Fixed station/outlet order matching the railway line layout.
const LOCATION_ORDER = ["Pickering", "Levisham", "Goathland", "Grosmont", "Shed Shop", "Whitby", "Shopify", "Mail Order"];
function locationRank(loc: string): number {
  const l = loc.toLowerCase();
  const i = LOCATION_ORDER.findIndex((k) => l.includes(k.toLowerCase()));
  return i === -1 ? LOCATION_ORDER.length : i;
}

/** "2026-03" -> "Mar 2026"; "2026-03-04" -> "Sat 4 Mar". */
function monthLabel(key: string): string {
  const [y, m] = key.split("-").map(Number);
  return `${MONTHS[m - 1]} ${y}`;
}
function dayLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const dow = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getUTCDay()];
  const [, m, dd] = iso.split("-").map(Number);
  return `${dow} ${dd} ${MONTHS[m - 1]}`;
}
function lastDayIso(ym: string): string {
  const [y, m] = ym.split("-").map(Number);
  const day = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${ym}-${String(day).padStart(2, "0")}`;
}
/** calendar month 1-12 -> fiscal month index 0-11 (0 = Mar). */
function fiscalMonthIndex(calMonth: number): number {
  return calMonth >= 3 ? calMonth - 3 : calMonth + 9;
}

function currentFiscalYear(): number {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth() + 1;
  return m >= 3 ? y + 1 : y;
}
function prettyDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

/** Budget for a date range, phasing each month's budget across its days. */
function budgetForRange(line: BudgetLine, fromISO: string, toISO: string): number {
  const from = new Date(`${fromISO}T00:00:00Z`);
  const to = new Date(`${toISO}T00:00:00Z`);
  let total = 0;
  const cur = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), 1));
  while (cur <= to) {
    const y = cur.getUTCFullYear();
    const m = cur.getUTCMonth() + 1;
    const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const monthStart = new Date(Date.UTC(y, m - 1, 1));
    const monthEnd = new Date(Date.UTC(y, m - 1, daysInMonth));
    const lo = from > monthStart ? from : monthStart;
    const hi = to < monthEnd ? to : monthEnd;
    const days = Math.floor((hi.getTime() - lo.getTime()) / 86400000) + 1;
    if (days > 0) total += (line.months[fiscalMonthIndex(m)] ?? 0) * (days / daysInMonth);
    cur.setUTCMonth(cur.getUTCMonth() + 1);
  }
  return total;
}

function variancePct(actual: number, budget: number): number | null {
  if (!budget) return null;
  return ((actual - budget) / budget) * 100;
}

/** This-month / to-date (day-phased) / full-year budget for one line. */
function tilesFor(line: BudgetLine): { thisMonth: number; toDate: number; fullYear: number } {
  const now = new Date();
  const calMonth = now.getUTCMonth() + 1;
  const idx = fiscalMonthIndex(calMonth);
  const dayOfMonth = now.getUTCDate();
  const daysInMonth = new Date(Date.UTC(now.getUTCFullYear(), calMonth, 0)).getUTCDate();
  const thisMonth = line.months[idx] ?? 0;
  let toDate = 0;
  for (let i = 0; i < idx; i++) toDate += line.months[i] ?? 0;
  toDate += thisMonth * (dayOfMonth / daysInMonth);
  return { thisMonth, toDate, fullYear: line.total };
}

// ---------------------------------------------------------------------------
// Gift-card position (all-time), from cache.GiftCardWriteDowns via
// /api/dashboards/sales/gift-cards. A point-in-time liability, so not scoped to
// the selected season.
interface GiftCardSummary {
  sales: number;         // value loaded onto cards (Bought)
  redemptions: number;   // value spent as payment (positive)
  expired: number;       // auto write-downs after 2yr (positive)
  refunds: number;       // net refunds/top-ups adjustment (positive = net out)
  outstanding: number;   // net still on cards = sales - redemptions - refunds - expired
  cards: number;
  generated_at: string | null;
}

// Reconciliation for the "Till Takings (Removed from TANC)" panel — period-scoped
// Retail till-takings minus the posted nominal ledger, from
// /api/dashboards/sales/reconciliation.
interface ReconResponse {
  from: string;
  to: string;
  till_net: number;
  tanc_net: number;
  removed: number;
  buckets: { giftcards: number; donations: number; food: number; other: number };
  tanc_available: boolean;
}

// iplicit-only RETAIL income lines (monthly) that don't come through MERAC, so
// the TANC total can be reconciled up to the iplicit management P&L. From
// /api/dashboards/sales/pnl-adjustments.
interface PnlAdjMonth { month: string; shopTotal: number; meracShop: number; expiredGiftCards: number; notThroughMerac: number; cateringTotal: number; meracCatering: number }
interface PnlAdjResponse { fiscalYear: number; season: string; months: PnlAdjMonth[] }

// Component
// ---------------------------------------------------------------------------
// "Refresh today" (2026-09-25, reports db/106): a row in app.RefreshRequests (kind
// 'epos-today') that the Sync Hub runs as job 29 — TODAY's rows of the nine Retail /
// Tea Room caches rebuilt from the live MERAC till lines; earlier days untouched.
// Once an hour for everyone; anyone who can see the tab may press it. Every viewer
// polls the shared status so a run anyone started reloads all open pages.
interface EposRefreshStatus {
  in_flight: boolean;
  latest: { status: string; requested_by: string; requested_at: string | null; completed_at: string | null; message: string | null; duration_ms: number | null } | null;
  today_date: string;
  today_as_of: string | null;
  last_full_refresh: string | null;
  typical_duration_ms: number | null;
  lockout_minutes: number;
  next_available_at: string | null;
  locked: boolean;
  can_request: boolean;
}
const EPOS_REFRESH_HOVER =
  "Updates today's shop and tea-room sales only, live from the MERAC tills. The overnight job cannot be permitted to run during the day due to performance hits on the MERAC server; earlier days (and any historic changes) come with the overnight job. Once an hour.";
function prettyUpn(upn: string | null | undefined): string {
  if (!upn) return "someone";
  const local = upn.split("@")[0];
  return local.split(/[._-]+/).filter(Boolean).map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(" ") || upn;
}

export function SalesDashboard() {
  const [fiscalYear, setFiscalYear] = useState<number>(() => currentFiscalYear());
  const [data, setData] = useState<SalesResponse | null>(null);
  // Refresh today — shared status, polled; reloadNonce re-fetches the data when a run completes.
  const [reloadNonce, setReloadNonce] = useState(0);
  const [rs, setRs] = useState<EposRefreshStatus | null>(null);
  const [posting, setPosting] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState<string | null>(null);
  const lastTodayRef = useRef<string | null>(null);
  const pollStatus = useCallback(async () => {
    try {
      const res = await fetch("/api/dashboards/sales/epos/refresh", { credentials: "include" });
      if (!res.ok) return;
      const s = (await res.json()) as EposRefreshStatus;
      setRs(s);
      if (s.today_as_of && lastTodayRef.current && s.today_as_of > lastTodayRef.current) setReloadNonce((n) => n + 1);
      if (s.today_as_of) lastTodayRef.current = s.today_as_of;
    } catch { /* transient */ }
  }, []);
  const inFlight = rs?.in_flight ?? false;
  useEffect(() => {
    void pollStatus();
    const t = globalThis.setInterval(() => void pollStatus(), inFlight ? 5000 : 15000);
    return () => globalThis.clearInterval(t);
  }, [pollStatus, inFlight]);
  const refreshBusy = inFlight || posting;
  const refreshLocked = rs?.locked ?? false;
  const onRefresh = useCallback(async () => {
    if (refreshBusy || refreshLocked) return;
    setPosting(true);
    setRefreshMsg(null);
    try {
      const res = await fetch("/api/dashboards/sales/epos/refresh", { method: "POST", credentials: "include", headers: { Accept: "application/json" } });
      if (!res.ok && res.status !== 202) {
        const b = await res.json().catch(() => ({} as { error?: string }));
        throw new Error(b.error || `Request failed (${res.status})`);
      }
      await pollStatus();
    } catch (err) {
      setRefreshMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setPosting(false);
    }
  }, [refreshBusy, refreshLocked, pollStatus]);
  const [budget, setBudget] = useState<BudgetResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [measure, setMeasure] = useState<Measure>("Net");
  // Basis dropdown (Default / Kathryn's Variant 1) — applies to every figure, table, popup and download.
  const [basis, setBasis] = useState<Basis>("saletype");
  // Compare against season (plan §1.5): last season's figures next to this season's.
  const [compare, setCompare] = useState(false);
  const [align, setAlign] = useState<CompareAlign>("calendar");
  const [lyData, setLyData] = useState<SalesResponse | null>(null);
  const [roundUp, setRoundUp] = useState(true);
  const [breakdownMode, setBreakdownMode] = useState(false);
  // Data source: 'till' (EPOS till-takings, default) or 'tanc' (posted nominal
  // ledger, ties to Finance). Toggled next to Breakdown.
  const [source, setSource] = useState<"till" | "tanc">("till");
  const [monthKey, setMonthKey] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [hidden, setHidden] = useState<Record<Category, Set<string>>>({ Retail: new Set(), "Tea Room": new Set() });
  // Hidden CLASSES per category (tea-room table shows Catering/Retail pills).
  const [classHidden, setClassHidden] = useState<Record<Category, Set<SaleClass>>>({ Retail: new Set(), "Tea Room": new Set() });

  // Breakdown popup.
  const [bd, setBd] = useState<{ title: string; loading: boolean; error: string | null; rows: BreakdownItem[]; total: number } | null>(null);

  // Gift-card position (all-time) — fetched once, not season-scoped.
  const [giftCards, setGiftCards] = useState<GiftCardSummary | null>(null);
  // Reconciliation (period-scoped) for the Removed-from-TANC panel.
  const [recon, setRecon] = useState<ReconResponse | null>(null);
  // iplicit P&L adjustments (monthly) + the Expired-Gift-Card include toggle.
  const [pnlAdj, setPnlAdj] = useState<PnlAdjResponse | null>(null);
  const [includeExpired, setIncludeExpired] = useState(true);

  // Product Filters (2026-09-17): sales elements as "Category:Analysis" pairs and
  // at most one special segment — Thank You voucher sales or Member sales. An
  // empty set with no segment is "All items": the unfiltered caches, the page
  // exactly as before. Filters need the till basis (TANC has no product detail).
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [analysisSel, setAnalysisSel] = useState<Set<string>>(() => new Set());
  const [segment, setSegment] = useState<Segment | null>(null);
  const analysisKey = useMemo(() => [...analysisSel].sort().join("|"), [analysisSel]);
  const filterActive = source === "till" && (analysisSel.size > 0 || segment != null);
  const [analyses, setAnalyses] = useState<AnalysesResponse | null>(null);
  // Thank You vouchers: the whole season (the sub-line on every outlet card) and
  // the shown period (the box). Guide Books: season + shown period (the box).
  const [tyvSeason, setTyvSeason] = useState<TyvResponse | null>(null);
  const [tyvPeriod, setTyvPeriod] = useState<{ data: TyvResponse | null; loading: boolean; error: string | null }>({ data: null, loading: false, error: null });
  const [tyvPdfBusy, setTyvPdfBusy] = useState(false);
  const [guide, setGuide] = useState<{ data: GuideBooksResponse | null; loading: boolean; error: string | null }>({ data: null, loading: false, error: null });
  const [gcOverviewOpen, setGcOverviewOpen] = useState(false);

  // Fetch a season + its budget.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const withCompare = compare && source === "till";
    Promise.all([
      fetch(
        `/api/dashboards/sales/epos?fiscalYear=${fiscalYear}&source=${source}` +
          (source === "till" && basis === "dept" ? `&basis=dept` : "") +
          (source === "till" && analysisKey ? `&analysis=${encodeURIComponent(analysisKey)}` : "") +
          (source === "till" && segment && basis !== "dept" ? `&segment=${segment}` : "") +
          (withCompare ? `&compare=ly&align=${align}` : ""),
        { credentials: "include" }
      ).then(async (res) => {
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          throw new Error(b.error || `Request failed (${res.status})`);
        }
        const body = (await res.json()) as SalesResponse | CompareResponse;
        if (withCompare && "current" in body) {
          setLyData(body.last_season);
          return body.current;
        }
        setLyData(null);
        return body as SalesResponse;
      }),
      fetch(`/api/dashboards/sales/budget?fiscalYear=${fiscalYear}`, { credentials: "include" })
        .then((res) => (res.ok ? (res.json() as Promise<BudgetResponse>) : null))
        .catch(() => null),
    ])
      .then(([d, b]) => {
        if (cancelled) return;
        setData(d);
        setBudget(b && b.has_budget ? b : null);
        if (d.fiscalYear !== fiscalYear) setFiscalYear(d.fiscalYear);
        setHidden({ Retail: new Set(), "Tea Room": new Set() });
        setClassHidden({ Retail: new Set(), "Tea Room": new Set() });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setData(null);
        setBudget(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fiscalYear, source, basis, compare, align, analysisKey, segment, reloadNonce]);

  // Gift-card position — one-off, independent of the season selector.
  useEffect(() => {
    let cancelled = false;
    fetch(`/api/dashboards/sales/gift-cards`, { credentials: "include" })
      .then((res) => (res.ok ? (res.json() as Promise<GiftCardSummary>) : null))
      .then((gc) => { if (!cancelled && gc) setGiftCards(gc); })
      .catch(() => { /* non-critical panel */ });
    return () => { cancelled = true; };
  }, []);

  // iplicit P&L adjustments for the season (monthly) — powers the P&L reconciliation.
  useEffect(() => {
    let cancelled = false;
    fetch(`/api/dashboards/sales/pnl-adjustments?fiscalYear=${fiscalYear}`, { credentials: "include" })
      .then((res) => (res.ok ? (res.json() as Promise<PnlAdjResponse>) : null))
      .then((p) => { if (!cancelled) setPnlAdj(p); })
      .catch(() => { if (!cancelled) setPnlAdj(null); });
    return () => { cancelled = true; };
  }, [fiscalYear]);

  const rows = data?.rows ?? [];

  // Sales elements for the Product Filters matrix (till basis only).
  useEffect(() => {
    let cancelled = false;
    if (source !== "till") { setAnalyses(null); return; }
    fetch(`/api/dashboards/sales/epos/analyses?fiscalYear=${fiscalYear}${basis === "dept" ? "&basis=dept" : ""}`, { credentials: "include" })
      .then((res) => (res.ok ? (res.json() as Promise<AnalysesResponse>) : null))
      .then((a) => { if (!cancelled) setAnalyses(a); })
      .catch(() => { if (!cancelled) setAnalyses(null); });
    return () => { cancelled = true; };
  }, [fiscalYear, source, basis]);

  // Last season's rows, moved onto this season's calendar so every filter, bucket and
  // matrix below treats them exactly like this season's (one code path for both).
  // In the running season the comparison stops at today (shifted), so a month in
  // progress is not set against a whole month last year; the window label says so.
  const todayIso = new Date().toISOString().slice(0, 10);
  const lyCapped = fiscalYear === currentFiscalYear();
  const lyRows = useMemo<SalesRow[]>(
    () => (lyData
      ? lyData.rows.map((r) => ({ ...r, SaleDate: shiftForward(r.SaleDate, align) })).filter((r) => !lyCapped || r.SaleDate <= todayIso)
      : []),
    [lyData, align, lyCapped, todayIso]
  );
  const onBasisChange = (b: Basis) => {
    setBasis(b);
    setMeasure(b === "dept" ? "Gross" : "Net"); // Variant 1 headline is Gross (Power BI shows till takings)
    if (b === "dept" && segment) setSegment(null); // Member / Thank You segments exist on the Default basis only
  };

  // Thank You vouchers for the whole season — the sub-line on each outlet card.
  useEffect(() => {
    let cancelled = false;
    fetch(`/api/dashboards/sales/thank-you-vouchers?fiscalYear=${fiscalYear}`, { credentials: "include" })
      .then((res) => (res.ok ? (res.json() as Promise<TyvResponse>) : null))
      .then((t) => { if (!cancelled) setTyvSeason(t); })
      .catch(() => { if (!cancelled) setTyvSeason(null); });
    return () => { cancelled = true; };
  }, [fiscalYear]);

  const months = useMemo(() => {
    const s = new Set<string>();
    for (const r of rows) s.add(r.SaleDate.slice(0, 7));
    return [...s].sort();
  }, [rows]);

  const seasonBounds = useMemo(() => {
    if (rows.length === 0) return { min: undefined as string | undefined, max: undefined as string | undefined };
    let min = rows[0].SaleDate;
    let max = rows[0].SaleDate;
    for (const r of rows) {
      if (r.SaleDate < min) min = r.SaleDate;
      if (r.SaleDate > max) max = r.SaleDate;
    }
    return { min, max };
  }, [rows]);

  useEffect(() => {
    if (months.length === 0) {
      setMonthKey("");
      return;
    }
    const nowKey = new Date().toISOString().slice(0, 7);
    setMonthKey(months.includes(nowKey) ? nowKey : months[months.length - 1]);
    setDateFrom("");
    setDateTo("");
  }, [months]);

  const usingRange = Boolean(dateFrom && dateTo && dateFrom <= dateTo);
  const dayGrain = usingRange || monthKey !== "";

  const scopeRows = useCallback((rowsIn: SalesRow[]) => {
    return rowsIn.filter((r) => {
      if (usingRange) return r.SaleDate >= dateFrom && r.SaleDate <= dateTo;
      if (monthKey) return r.SaleDate.slice(0, 7) === monthKey;
      return true;
    });
  }, [usingRange, dateFrom, dateTo, monthKey]);
  const scopedRows = useMemo(() => scopeRows(rows), [rows, scopeRows]);
  const lyScopedRows = useMemo(() => scopeRows(lyRows), [lyRows, scopeRows]);

  const locationsByCat = useMemo(() => {
    const sets: Record<Category, Set<string>> = { Retail: new Set(), "Tea Room": new Set() };
    for (const r of rows) sets[r.Category]?.add(r.Location);
    const out = {} as Record<Category, string[]>;
    for (const c of CATEGORIES) {
      out[c] = [...sets[c]].sort((a, b) => locationRank(a) - locationRank(b) || a.localeCompare(b));
    }
    return out;
  }, [rows]);

  // Build a matrix for one category: buckets (rows) × visible locations, honouring
  // location + class filters. Returns the displayed-measure grid and a Net total
  // (for budget variance, which is always against Net), the trading days in the
  // period (days with any sale in the category — Easter moves, so Compare shows a
  // per-trading-day average too) and, on the dept basis, the walk-up share per
  // location. The SAME function builds this season's and last season's matrices.
  const buildMatrix = useCallback(
    (cat: Category, rowsIn: SalesRow[]): Matrix => {
      const visible = locationsByCat[cat].filter((l) => !hidden[cat].has(l));
      const bucketMap = new Map<string, Map<string, number>>();
      const walkUpByLoc = new Map<string, number>();
      const days = new Set<string>();
      const walkKey = measure === "Gross" ? "WalkUpGross" : "WalkUpNet";
      let netTotal = 0;
      for (const r of rowsIn) {
        if (r.Category !== cat) continue;
        if (hidden[cat].has(r.Location)) continue;
        if (classHidden[cat].has(r.Class)) continue;
        const bk = dayGrain ? r.SaleDate : r.SaleDate.slice(0, 7);
        if (!bucketMap.has(bk)) bucketMap.set(bk, new Map());
        const lm = bucketMap.get(bk)!;
        lm.set(r.Location, (lm.get(r.Location) ?? 0) + r[measure]);
        walkUpByLoc.set(r.Location, (walkUpByLoc.get(r.Location) ?? 0) + (r[walkKey] ?? 0));
        netTotal += r.Net;
        days.add(r.SaleDate);
      }
      const buckets = [...bucketMap.keys()].sort();
      const grid = buckets.map((bk) => {
        const lm = bucketMap.get(bk)!;
        const cells = visible.map((loc) => lm.get(loc) ?? 0);
        const rowTotal = cells.reduce((a, b) => a + b, 0);
        return { key: bk, label: dayGrain ? dayLabel(bk) : monthLabel(bk), cells, rowTotal };
      });
      const colTotals = visible.map((_, i) => grid.reduce((a, g) => a + g.cells[i], 0));
      const grandTotal = colTotals.reduce((a, b) => a + b, 0);
      const walkUpCols = visible.map((loc) => walkUpByLoc.get(loc) ?? 0);
      const walkUpTotal = walkUpCols.reduce((a, b) => a + b, 0);
      return { visible, grid, colTotals, grandTotal, netTotal, tradingDays: days.size, walkUpCols, walkUpTotal };
    },
    [locationsByCat, hidden, classHidden, dayGrain, measure]
  );

  const retailMatrix = useMemo(() => buildMatrix("Retail", scopedRows), [buildMatrix, scopedRows]);
  const teaMatrix = useMemo(() => buildMatrix("Tea Room", scopedRows), [buildMatrix, scopedRows]);
  const lyRetailMatrix = useMemo(() => (lyData ? buildMatrix("Retail", lyScopedRows) : null), [buildMatrix, lyScopedRows, lyData]);
  const lyTeaMatrix = useMemo(() => (lyData ? buildMatrix("Tea Room", lyScopedRows) : null), [buildMatrix, lyScopedRows, lyData]);

  // Season summary — retail by location (whole season, in the chosen measure), with the
  // walk-up share on the dept basis. Same function for this season and last season.
  const summarise = useCallback((rowsIn: SalesRow[]) => {
    const m = new Map<string, number>();
    const w = new Map<string, number>();
    const walkKey = measure === "Gross" ? "WalkUpGross" : "WalkUpNet";
    for (const r of rowsIn) {
      if (r.Category !== "Retail") continue;
      m.set(r.Location, (m.get(r.Location) ?? 0) + r[measure]);
      w.set(r.Location, (w.get(r.Location) ?? 0) + (r[walkKey] ?? 0));
    }
    const items = [...m.entries()]
      .map(([location, value]) => ({ location, value, walkUp: w.get(location) ?? 0 }))
      .sort((a, b) => locationRank(a.location) - locationRank(b.location) || a.location.localeCompare(b.location));
    const total = items.reduce((a, b) => a + b.value, 0);
    const walkUp = items.reduce((a, b) => a + b.walkUp, 0);
    const max = items.reduce((a, b) => Math.max(a, b.value), 0);
    return { items, total, max, walkUp };
  }, [measure]);
  const summary = useMemo(() => summarise(rows), [summarise, rows]);
  const lySummary = useMemo(() => (lyData ? summarise(lyRows) : null), [summarise, lyRows, lyData]);
  const lySummaryByLoc = useMemo(() => new Map((lySummary?.items ?? []).map((it) => [it.location, it.value])), [lySummary]);

  // Today per outlet (2026-09-25): the rows dated today — live from MERAC after a
  // "Refresh today" (or the overnight run's rows for today before one). In the shown
  // measure, both categories, so each outlet tile can carry a small "Today" line.
  const todayByLocation = useMemo(() => {
    const today = rs?.today_date ?? new Date().toISOString().slice(0, 10);
    const m = new Map<string, number>();
    for (const r of rows) {
      if (r.SaleDate !== today) continue;
      m.set(r.Location, (m.get(r.Location) ?? 0) + r[measure]);
    }
    return m;
  }, [rows, measure, rs?.today_date]);

  // Thank You voucher sales per retail outlet, whole season, in the shown measure.
  const tyvByLocation = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of tyvSeason?.byLocation ?? []) {
      if (r.category === "Retail" && r.location) m.set(r.location, measure === "Gross" ? r.spendGross : r.spendNet);
    }
    return m;
  }, [tyvSeason, measure]);

  const filterLabel = useMemo(() => {
    if (!filterActive) return "";
    const parts: string[] = [];
    if (analysisSel.size > 0) parts.push(`${analysisSel.size} product${analysisSel.size === 1 ? "" : "s"}`);
    if (segment === "thankyou") parts.push("Thank You voucher sales");
    if (segment === "member") parts.push("Member sales");
    return parts.join(" · ");
  }, [filterActive, analysisSel, segment]);

  // iplicit P&L adjustments summed for the whole season (tiles) and for the
  // shown period (the P&L reconciliation under the Retail matrix). Monthly data,
  // so a custom day-range is summed by the months it spans.
  const adjSeason = useMemo(() => {
    const ms = pnlAdj?.months ?? [];
    return ms.reduce((a, m) => ({ expired: a.expired + m.expiredGiftCards, notMerac: a.notMerac + m.notThroughMerac }), { expired: 0, notMerac: 0 });
  }, [pnlAdj]);
  const adjPeriod = useMemo(() => {
    const ms = pnlAdj?.months ?? [];
    const inP = (m: string) => (usingRange ? m >= dateFrom.slice(0, 7) && m <= dateTo.slice(0, 7) : monthKey ? m === monthKey : true);
    return ms.filter((x) => inP(x.month)).reduce(
      (a, m) => ({
        shopTotal: a.shopTotal + m.shopTotal,
        meracShop: a.meracShop + m.meracShop,
        expired: a.expired + m.expiredGiftCards,
        notMerac: a.notMerac + m.notThroughMerac,
        cateringTotal: a.cateringTotal + m.cateringTotal,
        meracCatering: a.meracCatering + m.meracCatering,
      }),
      { shopTotal: 0, meracShop: 0, expired: 0, notMerac: 0, cateringTotal: 0, meracCatering: 0 }
    );
  }, [pnlAdj, usingRange, dateFrom, dateTo, monthKey]);

  // Per-category season Net actuals (for budget tiles) — total and to-date.
  const netByCat = useMemo(() => {
    const todayIso = new Date().toISOString().slice(0, 10);
    const out: Record<Category, { total: number; toDate: number }> = {
      Retail: { total: 0, toDate: 0 },
      "Tea Room": { total: 0, toDate: 0 },
    };
    for (const r of rows) {
      const o = out[r.Category];
      if (!o) continue;
      o.total += r.Net;
      if (r.SaleDate <= todayIso) o.toDate += r.Net;
    }
    return out;
  }, [rows]);

  const toggleLoc = (cat: Category, loc: string) => {
    setHidden((prev) => {
      const next = new Set(prev[cat]);
      if (next.has(loc)) next.delete(loc);
      else next.add(loc);
      return { ...prev, [cat]: next };
    });
  };
  const toggleClass = (cat: Category, c: SaleClass) => {
    setClassHidden((prev) => {
      const next = new Set(prev[cat]);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return { ...prev, [cat]: next };
    });
  };

  const rangeLabel = usingRange
    ? `${dayLabel(dateFrom)} – ${dayLabel(dateTo)}`
    : monthKey
      ? monthLabel(monthKey)
      : `Season ${data?.fiscalYearLabel ?? ""}`;

  // Effective date window for the Movers & Shakers query (follows the page's
  // month / custom-range / whole-season selection).
  const effRange = useMemo(() => {
    if (usingRange) return { from: dateFrom, to: dateTo };
    if (monthKey) return { from: `${monthKey}-01`, to: lastDayIso(monthKey) };
    return { from: seasonBounds.min ?? "", to: seasonBounds.max ?? "" };
  }, [usingRange, dateFrom, dateTo, monthKey, seasonBounds]);

  // Reconciliation for the Removed-from-TANC panel — follows the displayed
  // period. Independent of the source toggle (it always compares both bases).
  useEffect(() => {
    let cancelled = false;
    if (!effRange.from || !effRange.to) { setRecon(null); return; }
    fetch(`/api/dashboards/sales/reconciliation?from=${effRange.from}&to=${effRange.to}`, { credentials: "include" })
      .then((res) => (res.ok ? (res.json() as Promise<ReconResponse>) : null))
      .then((r) => { if (!cancelled) setRecon(r); })
      .catch(() => { if (!cancelled) setRecon(null); });
    return () => { cancelled = true; };
  }, [effRange.from, effRange.to]);

  // Thank You voucher sales for the shown period — the box.
  useEffect(() => {
    let cancelled = false;
    if (!effRange.from || !effRange.to) { setTyvPeriod({ data: null, loading: false, error: null }); return; }
    setTyvPeriod((p) => ({ ...p, loading: true, error: null }));
    fetch(`/api/dashboards/sales/thank-you-vouchers?from=${effRange.from}&to=${effRange.to}`, { credentials: "include" })
      .then(async (res) => { if (!res.ok) throw new Error(`Request failed (${res.status})`); return res.json() as Promise<TyvResponse>; })
      .then((t) => { if (!cancelled) setTyvPeriod({ data: t, loading: false, error: null }); })
      .catch((e) => { if (!cancelled) setTyvPeriod({ data: null, loading: false, error: e instanceof Error ? e.message : String(e) }); });
    return () => { cancelled = true; };
  }, [effRange.from, effRange.to]);

  // Guide Book sales — season + the shown period.
  useEffect(() => {
    let cancelled = false;
    if (!effRange.from || !effRange.to) { setGuide({ data: null, loading: false, error: null }); return; }
    setGuide((p) => ({ ...p, loading: true, error: null }));
    fetch(`/api/dashboards/sales/guide-books?fiscalYear=${fiscalYear}&from=${effRange.from}&to=${effRange.to}`, { credentials: "include" })
      .then(async (res) => { if (!res.ok) throw new Error(`Request failed (${res.status})`); return res.json() as Promise<GuideBooksResponse>; })
      .then((g) => { if (!cancelled) setGuide({ data: g, loading: false, error: null }); })
      .catch((e) => { if (!cancelled) setGuide({ data: null, loading: false, error: e instanceof Error ? e.message : String(e) }); });
    return () => { cancelled = true; };
  }, [fiscalYear, effRange.from, effRange.to]);

  // Thank You voucher PDF — fetch the per-transaction detail on demand, then download.
  const onTyvPdf = useCallback(async () => {
    if (!effRange.from || !effRange.to || tyvPdfBusy) return;
    setTyvPdfBusy(true);
    try {
      const res = await fetch(`/api/dashboards/sales/thank-you-vouchers?from=${effRange.from}&to=${effRange.to}&detail=1`, { credentials: "include" });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const t = (await res.json()) as TyvResponse;
      downloadTyvPdf(t, { rangeLabel, seasonLabel: data?.fiscalYearLabel ?? "" });
    } catch (e) {
      setTyvPeriod((p) => ({ ...p, error: e instanceof Error ? e.message : String(e) }));
    } finally {
      setTyvPdfBusy(false);
    }
  }, [effRange.from, effRange.to, tyvPdfBusy, rangeLabel, data?.fiscalYearLabel]);

  // Budget for the currently-viewed period, per category line.
  function periodBudget(line: BudgetLine | undefined): number | null {
    if (!line) return null;
    if (usingRange) return budgetForRange(line, dateFrom, dateTo);
    if (monthKey) return line.months[fiscalMonthIndex(Number(monthKey.slice(5, 7)))] ?? 0;
    return line.total;
  }
  const retailPeriodBudget = periodBudget(budget?.retail);
  const teaPeriodBudget = periodBudget(budget?.teaRoom);

  // Budget tiles (Shop Sales). This-month / to-date only meaningful for the
  // current running season; a past season shows just the full-year figure.
  const isCurrentSeason = fiscalYear === currentFiscalYear();
  const budgetTiles = useMemo(() => (budget ? tilesFor(budget.retail) : null), [budget]);
  const teaBudgetTiles = useMemo(() => (budget ? tilesFor(budget.teaRoom) : null), [budget]);

  // The period Compare is lined up against (labels only — the data came with the season fetch).
  const periodWindows = useMemo<CompareWindows | null>(
    () => (lyData && effRange.from && effRange.to
      ? lyWindow(effRange.from, lyCapped && effRange.to > todayIso && effRange.from <= todayIso ? todayIso : effRange.to, align)
      : null),
    [lyData, effRange.from, effRange.to, align, lyCapped, todayIso]
  );
  const exportCtx = { measure, retailMatrix, teaMatrix, rangeLabel, basis, lyRetail: lyRetailMatrix, lyTea: lyTeaMatrix, lyLabel: lyData ? `Last season ${lyData.fiscalYearLabel}${align === "week" ? " (same weeks)" : ""}` : null };
  const onExcel = useCallback(() => {
    if (!data) return;
    void exportExcel(data, { ...exportCtx, summary, lySummary });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, measure, retailMatrix, teaMatrix, summary, lySummary, rangeLabel, basis, lyRetailMatrix, lyTeaMatrix, lyData, align]);
  const onPdf = useCallback(() => {
    if (!data) return;
    exportPdf(data, { ...exportCtx, roundUp });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, measure, retailMatrix, teaMatrix, rangeLabel, roundUp, basis, lyRetailMatrix, lyTeaMatrix, lyData, align]);

  // Fetch a breakdown for a clicked cell / total.
  const openBreakdown = useCallback(
    (category: Category, locations: string, from: string, to: string, cls: string, title: string) => {
      setBd({ title, loading: true, error: null, rows: [], total: 0 });
      const qs = new URLSearchParams({ category, from, to });
      if (locations) qs.set("locations", locations);
      if (cls) qs.set("class", cls);
      if (basis === "dept") qs.set("basis", "dept");
      fetch(`/api/dashboards/sales/epos/breakdown?${qs.toString()}`, { credentials: "include" })
        .then(async (res) => {
          if (!res.ok) {
            const b = await res.json().catch(() => ({}));
            throw new Error(b.error || `Request failed (${res.status})`);
          }
          return res.json() as Promise<{ rows: BreakdownItem[] }>;
        })
        .then((r) => {
          const brows = r.rows ?? [];
          const total = brows.reduce((a, x) => a + x[measure === "Gross" ? "Gross" : "Net"], 0);
          setBd({ title, loading: false, error: null, rows: brows, total });
        })
        .catch((err) => setBd({ title, loading: false, error: err instanceof Error ? err.message : String(err), rows: [], total: 0 }));
    },
    [measure, basis]
  );

  const hasData = !loading && !error && rows.length > 0;

  return (
    <main className="app-main sales">
      <div className="sales-head">
        <div>
          <p className="sales-sub">
            Daily shop and tea-room takings by location,{" "}
            {source === "tanc"
              ? "from the posted nominal ledger (TANC — ties to Finance)"
              : "from the EPOS tills"}. Totals shown as{" "}
            <strong>{measure === "Net" ? "Net (ex-VAT)" : "Gross (incl VAT)"}</strong>.
            {breakdownMode ? " Click any figure for its sales-element breakdown." : ""}
          </p>
          <div className="sales-basis">
            <label className="sales-field">
              <span>Figures</span>
              <select
                value={basis}
                onChange={(e) => onBasisChange(e.target.value as Basis)}
                disabled={source === "tanc"}
                aria-label="Which basis the figures are counted on"
                title={source === "tanc" ? "The TANC nominal ledger has one basis; switch to Till-takings to use Kathryn's Variant 1" : "Default (sale-type basis, Net) or Kathryn's Variant 1 (department basis incl. walk-up tickets, Gross)"}
              >
                {(["saletype", "dept"] as Basis[]).map((b) => <option key={b} value={b}>{BASIS_LABEL[b]}</option>)}
              </select>
            </label>
            <span className="sales-basis-note">{BASIS_NOTE[basis]}</span>
          </div>
        </div>
        <div className="sales-refresh-note">
          <div className="sales-refresh-row">
            <button
              type="button"
              className="sales-refresh-btn"
              onClick={() => void onRefresh()}
              disabled={refreshBusy || refreshLocked}
              title={EPOS_REFRESH_HOVER}
            >
              {refreshBusy ? "↻ Refreshing…" : "↻ Refresh today"}
            </button>
          </div>
          Last refreshed {prettyDateTime(data?.last_refresh ?? null)}
          {source === "tanc" ? " (TANC nominal ledger)" : ""}. Updates automatically each morning.
          {rs && (
            <>
              {" "}Today's sales as of <strong>{rs.today_as_of ? prettyDateTime(rs.today_as_of) : "the overnight run"}</strong>
              {rs.latest?.status === "completed" && rs.latest.completed_at && rs.today_as_of
                && Math.abs(new Date(rs.latest.completed_at).getTime() - new Date(rs.today_as_of).getTime()) < 5 * 60_000
                ? ` (refreshed by ${prettyUpn(rs.latest.requested_by)}${rs.latest.duration_ms != null ? `, took ${Math.max(1, Math.round(rs.latest.duration_ms / 1000))} s` : ""})`
                : ""}.
              {" "}
              {inFlight
                ? `Refreshing now — started by ${prettyUpn(rs.latest?.requested_by)}; the page reloads when it finishes.`
                : refreshLocked && rs.next_available_at
                ? <>Next refresh permitted <strong>{prettyDateTime(rs.next_available_at)}</strong>.</>
                : <strong>Available now.</strong>}
              {rs.latest?.status === "error" && !inFlight ? <span className="sales-refresh-err"> Last run failed{rs.latest.message ? ` (${rs.latest.message})` : ""}.</span> : ""}
              {" "}Takes about {rs.typical_duration_ms != null ? Math.max(1, Math.round(rs.typical_duration_ms / 1000)) : 30} seconds; once an hour.
            </>
          )}
          {refreshMsg && <span className="sales-refresh-err"> {refreshMsg}</span>}
        </div>
      </div>

      {/* Product Filters — sales elements + Thank You voucher / Member segments */}
      <ProductFilters
        open={filtersOpen}
        onToggle={() => setFiltersOpen((v) => !v)}
        analyses={analyses?.rows ?? null}
        selected={analysisSel}
        onSelectedChange={setAnalysisSel}
        segment={segment}
        onSegmentChange={setSegment}
        disabled={source === "tanc"}
        disabledReason="Product filters need the till-takings basis — the TANC nominal ledger has no per-product detail"
        segmentsDisabledReason={basis === "dept" ? "Member / Thank You voucher sales are counted on the Default basis only — switch Figures to Default to use them" : undefined}
        fmt={(n) => money(n, roundUp)}
      />

      {/* Season summary — retail by location + Shop budget */}
      <section className="sales-summary" aria-label="Season retail summary">
        <div className="sales-summary-head">
          <h2>
            Retail — season {data?.fiscalYearLabel ?? ""} by location
            {filterActive && <span className="sales-filter-chip" title="A product filter is active — every figure on this page is filtered">{filterLabel}</span>}
          </h2>
          <span className="sales-summary-total">
            {money(summary.total, roundUp)}
            {lySummary && <span className="sales-summary-ly"><LyCell current={summary.total} ly={lySummary.total} fmt={(n) => money(n, roundUp)} /></span>}
            {basis === "dept" && summary.walkUp !== 0 && (
              <span className="sales-walkup-chip" title="Ticket, car-park and booking lines rung through the shop tills (any sale type other than Shop) — included in these figures under Kathryn's Variant 1">
                of which walk-up tickets {money(summary.walkUp, roundUp)}
              </span>
            )}
          </span>
        </div>
        {budgetTiles && filterActive && (
          <div className="sales-filter-note">Budget comparison hidden while a product filter is active — budgets are for all items.</div>
        )}
        {budgetTiles && !filterActive && basis === "dept" && (
          <div className="sales-filter-note">Shop budget comparison hidden on Kathryn's Variant 1 — the Shop Sales budget is merchandise only, and these figures include walk-up tickets.</div>
        )}
        {budgetTiles && !filterActive && basis !== "dept" && (
          <div className="sales-budget-strip">
            {isCurrentSeason && (
              <div className="sales-btile">
                <div className="sales-btile-label">This month budget (Shop)</div>
                <div className="sales-btile-val">{money(budgetTiles.thisMonth, roundUp)}</div>
              </div>
            )}
            {isCurrentSeason && (
              <div className="sales-btile">
                <div className="sales-btile-label">Budget to date (Shop)</div>
                <div className="sales-btile-val">{money(budgetTiles.toDate, roundUp)}</div>
                <div className="sales-btile-sub">
                  actual {money(netByCat.Retail.toDate, roundUp)}{" "}
                  <Variance actual={netByCat.Retail.toDate} budget={budgetTiles.toDate} />
                </div>
              </div>
            )}
            <div className="sales-btile">
              <div className="sales-btile-label">Full-year budget (Shop)</div>
              <div className="sales-btile-val">{money(budgetTiles.fullYear, roundUp)}</div>
              <div className="sales-btile-sub">
                actual {money(netByCat.Retail.total, roundUp)}{" "}
                <Variance actual={netByCat.Retail.total} budget={budgetTiles.fullYear} />
              </div>
            </div>
          </div>
        )}
        <div className="sales-summary-tiles">
          {summary.items.map((it) => (
            <div className="sales-tile" key={it.location} title={`${it.location}: ${money(it.value, roundUp)}`}>
              <div className="sales-tile-loc">{it.location}</div>
              <div className="sales-tile-val">{money(it.value, roundUp)}</div>
              <div className="sales-tile-bar">
                <span style={{ width: `${summary.max > 0 ? (it.value / summary.max) * 100 : 0}%` }} />
              </div>
              {lySummary && (
                <div className="sales-tile-ly" title="Last season, same period">
                  Last season <LyCell current={it.value} ly={lySummaryByLoc.get(it.location) ?? 0} fmt={(n) => money(n, roundUp)} />
                </div>
              )}
              {basis === "dept" && it.walkUp !== 0 && (
                <div className="sales-tile-walkup" title="Ticket, car-park and booking lines rung through this shop till — included in the figure above">
                  incl. walk-up tickets <strong>{money(it.walkUp, roundUp)}</strong> ({it.value ? Math.round((it.walkUp / it.value) * 100) : 0}%)
                </div>
              )}
              <div className="sales-tile-today" title={`Today's sales at ${it.location} (${measure}) — ${rs?.today_as_of ? `live from MERAC as of ${prettyDateTime(rs.today_as_of)}` : "as at the overnight run"}`}>
                Today <strong>{todayByLocation.has(it.location) ? money(todayByLocation.get(it.location)!, roundUp) : "—"}</strong>
              </div>
              {tyvByLocation.has(it.location) && (
                <div className="sales-tile-sub" title="Sales in this shop paid with a Gift Aid Thank You voucher — whole season">
                  Thank You vouchers {money(tyvByLocation.get(it.location)!, roundUp)}
                </div>
              )}
            </div>
          ))}
          {summary.items.length === 0 && <div className="sales-empty">No retail sales in this season yet.</div>}
          {source === "tanc" && adjSeason.notMerac !== 0 && (
            <div className="sales-tile sales-tile--pnl" title="Shop sales booked directly in iplicit that don't come through MERAC (nominal 62010400). Season total. Always included in the P&L total.">
              <div className="sales-tile-loc">Not through Merac</div>
              <div className="sales-tile-val">{money(adjSeason.notMerac, roundUp)}</div>
            </div>
          )}
          {source === "tanc" && adjSeason.expired !== 0 && (
            <div className={"sales-tile sales-tile--pnl" + (includeExpired ? "" : " sales-tile--muted")} title="Expired gift-card breakage income (iplicit 69010625). Season total. Use the 'Expired Gift Card' button to include/exclude it from the P&L total.">
              <div className="sales-tile-loc">Expired gift cards{includeExpired ? "" : " (off)"}</div>
              <div className="sales-tile-val">{money(adjSeason.expired, roundUp)}</div>
            </div>
          )}
          {giftCards && (
            <div className="sales-tile sales-tile--gc" title="Gift-card position across the whole railway, all time — from the gift-card ledger (not a single shop or season).">
              <div className="sales-gc-head">
                Gift cards <span className="sales-gc-scope">all time</span>
                <button type="button" className="sales-gc-btn sales-gc-btn--head" onClick={() => setGcOverviewOpen(true)}
                  title="Gift-card sales by outlet — all outlets including Customer Services and online — all time, this season and this month">
                  Overview
                </button>
              </div>
              <div className="sales-gc-stats">
                <div className="sales-gc-stat">
                  <span className="sales-gc-k">Sales</span>
                  <span className="sales-gc-v">{money(giftCards.sales, roundUp)}</span>
                </div>
                <div className="sales-gc-stat">
                  <span className="sales-gc-k">Redemptions</span>
                  <span className="sales-gc-v">{money(giftCards.redemptions, roundUp)}</span>
                </div>
                <div className="sales-gc-stat sales-gc-stat--out">
                  <span className="sales-gc-k">Outstanding</span>
                  <span className="sales-gc-v">{money(giftCards.outstanding, roundUp)}</span>
                </div>
              </div>
              <div className="sales-gc-foot">
                unredeemed value on live cards · after {money(giftCards.expired, roundUp)} expired
                {giftCards.refunds ? <> · {money(giftCards.refunds, roundUp)} net refunds</> : null}
                {giftCards.generated_at ? <> · as at {prettyDateTime(giftCards.generated_at)}</> : null}
              </div>
            </div>
          )}
          {recon && recon.tanc_available && (
            <div className="sales-tile sales-tile--recon" title="Retail till-takings for the shown period that don't post to the shop-sales nominals — mostly gift cards, donations and food. This is why the till view reads higher than the TANC nominal.">
              <div className="sales-gc-head">
                Till takings <span className="sales-gc-scope sales-gc-scope--amber">removed from TANC</span>
              </div>
              <div className="sales-recon-total">{money(recon.removed, roundUp)}</div>
              <div className="sales-gc-foot">
                {rangeLabel} · till {money(recon.till_net, roundUp)} − TANC {money(recon.tanc_net, roundUp)}
                {(recon.buckets.giftcards || recon.buckets.donations || recon.buckets.food) ? (
                  <> · of which gift cards {money(recon.buckets.giftcards, roundUp)}, donations {money(recon.buckets.donations, roundUp)}, food {money(recon.buckets.food, roundUp)}</>
                ) : null}
              </div>
            </div>
          )}
          <ThankYouVoucherBox
            data={tyvPeriod.data}
            loading={tyvPeriod.loading}
            error={tyvPeriod.error}
            rangeLabel={rangeLabel}
            measure={measure}
            fmt={(n) => money(n, roundUp)}
            onPdf={() => void onTyvPdf()}
            pdfBusy={tyvPdfBusy}
          />
          <GuideBookBox data={guide.data} loading={guide.loading} error={guide.error} rangeLabel={rangeLabel} fmt={(n) => money(n, roundUp)} />
        </div>
      </section>

      {/* Controls */}
      <div className="sales-toolbar">
        <label className="sales-field">
          <span>Season</span>
          <select value={fiscalYear} onChange={(e) => setFiscalYear(Number(e.target.value))}>
            {(data?.years ?? [{ fiscalYear, label: "" }]).map((y) => (
              <option key={y.fiscalYear} value={y.fiscalYear}>{y.label || y.fiscalYear}</option>
            ))}
          </select>
        </label>
        <label className="sales-field">
          <span>Month</span>
          <select
            value={usingRange ? "__range__" : monthKey}
            onChange={(e) => {
              setDateFrom("");
              setDateTo("");
              setMonthKey(e.target.value === "__range__" ? "" : e.target.value);
            }}
          >
            <option value="">Whole season</option>
            {months.map((m) => (
              <option key={m} value={m}>{monthLabel(m)}</option>
            ))}
            {usingRange && <option value="__range__">Custom range</option>}
          </select>
        </label>
        <label className="sales-field">
          <span>From</span>
          <input type="date" value={dateFrom} min={seasonBounds.min} max={dateTo || seasonBounds.max} onChange={(e) => setDateFrom(e.target.value)} />
        </label>
        <label className="sales-field">
          <span>To</span>
          <input type="date" value={dateTo} min={dateFrom || seasonBounds.min} max={seasonBounds.max} onChange={(e) => setDateTo(e.target.value)} />
        </label>
        {usingRange && (
          <button type="button" className="sales-clear" onClick={() => { setDateFrom(""); setDateTo(""); }}>Clear range</button>
        )}
        <div className="sales-measure" role="group" aria-label="Sales measure">
          {(["Net", "Gross"] as Measure[]).map((m) => (
            <button key={m} type="button" className={"sales-measure-btn" + (measure === m ? " active" : "")} onClick={() => setMeasure(m)}>
              {m === "Net" ? "Net (ex VAT)" : "Gross (inc VAT)"}
            </button>
          ))}
        </div>
        {source === "till" && (
          <span className="sales-compare">
            <CompareToggle on={compare} onToggle={() => setCompare((v) => !v)} align={align} onAlign={setAlign} windows={periodWindows} />
          </span>
        )}
        <button
          type="button"
          className={"sales-pill sales-round" + (basis === "dept" ? "" : " off")}
          onClick={() => onBasisChange(basis === "dept" ? "saletype" : "dept")}
          aria-pressed={basis === "dept"}
          disabled={source === "tanc"}
          title={basis === "dept"
            ? "Walk-up tickets ON (Kathryn's Variant 1): ticket, car-park and booking lines rung through the shop tills are included — click to switch to Default"
            : "Walk-up tickets OFF (Default): only Shop / Tea Room sale types — click to include the walk-up tickets and car parking rung through the shop tills (Kathryn's Variant 1)"}
        >
          Walk-up tickets
        </button>
        <button
          type="button"
          className={"sales-pill sales-round" + (roundUp ? "" : " off")}
          onClick={() => setRoundUp((v) => !v)}
          aria-pressed={roundUp}
          title={roundUp ? "Showing whole pounds — click to show pence (£x.xx)" : "Showing pence (£x.xx) — click to round"}
        >
          Round up
        </button>
        <button
          type="button"
          className={"sales-pill sales-round" + (breakdownMode ? "" : " off")}
          onClick={() => setBreakdownMode((v) => !v)}
          aria-pressed={breakdownMode}
          disabled={source === "tanc"}
          title={source === "tanc"
            ? "Breakdown isn't available on the TANC nominal basis (the ledger has no per-product detail)"
            : "Breakdown mode — click any figure to see the sales elements making it up"}
        >
          Breakdown
        </button>
        <div className="sales-source" role="group" aria-label="Data source">
          <button
            type="button"
            className={"sales-source-btn" + (source === "till" ? " active" : "")}
            aria-pressed={source === "till"}
            onClick={() => setSource("till")}
            title="Till-takings — every completed EPOS shop/tea line (the operational view)"
          >
            Till-takings
          </button>
          <button
            type="button"
            className={"sales-source-btn" + (source === "tanc" ? " active" : "")}
            aria-pressed={source === "tanc"}
            onClick={() => { setSource("tanc"); setBreakdownMode(false); }}
            title="TANC Nominal — the posted nominal ledger, net + VAT split, that ties to Finance"
          >
            TANC Nominal
          </button>
        </div>
        {source === "tanc" && (
          <button
            type="button"
            className={"sales-pill sales-round" + (includeExpired ? "" : " off")}
            onClick={() => setIncludeExpired((v) => !v)}
            aria-pressed={includeExpired}
            title={includeExpired
              ? "Expired gift cards ON — the breakage income is included so the total matches the iplicit P&L. Click to exclude."
              : "Expired gift cards OFF — breakage excluded (pure nominal ledger). Click to include and match the iplicit P&L."}
          >
            Expired Gift Card {includeExpired ? "On" : "Off"}
          </button>
        )}
        <span className="sales-spacer" />
        <button type="button" className="sales-export" onClick={onExcel} disabled={!hasData}>⬇ Excel</button>
        <button type="button" className="sales-export" onClick={onPdf} disabled={!hasData}>🖨 PDF</button>
      </div>

      {error ? (
        <div className="sales-empty sales-empty--error">Couldn’t load sales: {error}</div>
      ) : loading ? (
        <div className="sales-empty">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="sales-empty">
          {source === "tanc"
            ? "No TANC nominal figures yet — the posted-ledger refresh hasn’t run for this season. Try another season, or switch back to Till-takings."
            : "No sales data for this season yet — it may not have refreshed. Try another season."}
        </div>
      ) : (
        <>
          <CategoryTable
            title="Retail Daily Sales"
            category="Retail"
            matrix={retailMatrix}
            allLocations={locationsByCat.Retail}
            hidden={hidden.Retail}
            onToggle={(loc) => toggleLoc("Retail", loc)}
            dayGrain={dayGrain}
            rangeLabel={rangeLabel}
            fmt={(n) => money(n, roundUp)}
            budget={basis === "dept" ? null : retailPeriodBudget}
            breakdownMode={breakdownMode}
            onBreakdown={openBreakdown}
            ly={lyRetailMatrix}
            lyLabel={lyData?.fiscalYearLabel ?? ""}
            showWalkUp={basis === "dept"}
          />
          {source === "tanc" && measure === "Net" && adjPeriod.shopTotal !== 0 && (() => {
            const tanc = retailMatrix.grandTotal;                                   // MERAC nominal ledger (MStore)
            const imbalance = adjPeriod.shopTotal - tanc - adjPeriod.notMerac - adjPeriod.expired;
            const pnlTotal = adjPeriod.shopTotal - (includeExpired ? 0 : adjPeriod.expired);
            return (
              <div className="sales-pnl-recon">
                <div className="sales-pnl-recon-head">Reconciliation to iplicit P&amp;L · {rangeLabel}</div>
                <div className="sales-pnl-row">
                  <span className="sales-pnl-k">
                    Merac shop sales (TANC) <span className="sales-pnl-iplicit">(iplicit {money(adjPeriod.meracShop, roundUp)})</span>
                  </span>
                  <span className="sales-pnl-v">{money(tanc, roundUp)}</span>
                </div>
                {adjPeriod.notMerac !== 0 && (
                  <div className="sales-pnl-row">
                    <span className="sales-pnl-k">+ Shop sales not through Merac</span>
                    <span className="sales-pnl-v">{money(adjPeriod.notMerac, roundUp)}</span>
                  </div>
                )}
                <div className={"sales-pnl-row" + (includeExpired ? "" : " sales-pnl-row--off")}>
                  <span className="sales-pnl-k">+ Expired gift cards {includeExpired ? "" : "(excluded)"}</span>
                  <span className="sales-pnl-v">{money(includeExpired ? adjPeriod.expired : 0, roundUp)}</span>
                </div>
                <div className="sales-pnl-row">
                  <span className="sales-pnl-k">Imbalance (overs/unders &amp; timing)</span>
                  <span className="sales-pnl-v">{money(imbalance, roundUp)}</span>
                </div>
                <div className="sales-pnl-row sales-pnl-total">
                  <span className="sales-pnl-k">P&amp;L total{includeExpired ? " — matches iplicit" : " (gift cards off)"}</span>
                  <span className="sales-pnl-v">{money(pnlTotal, roundUp)}</span>
                </div>
                <p className="sales-pnl-note">
                  Variances are overs &amp; unders and timing differences. iplicit figures are locked;
                  MERAC data is still subject to ad-hoc administrative changes.
                </p>
              </div>
            );
          })()}
          {teaBudgetTiles && (
            <div className="sales-teabudget">
              <div className="sales-teabudget-head">Tea Rooms — Catering budget (season {data?.fiscalYearLabel ?? ""})</div>
              <div className="sales-budget-strip">
                {isCurrentSeason && (
                  <div className="sales-btile">
                    <div className="sales-btile-label">This month budget</div>
                    <div className="sales-btile-val">{money(teaBudgetTiles.thisMonth, roundUp)}</div>
                  </div>
                )}
                {isCurrentSeason && (
                  <div className="sales-btile">
                    <div className="sales-btile-label">Budget to date</div>
                    <div className="sales-btile-val">{money(teaBudgetTiles.toDate, roundUp)}</div>
                    <div className="sales-btile-sub">
                      actual {money(netByCat["Tea Room"].toDate, roundUp)}{" "}
                      <Variance actual={netByCat["Tea Room"].toDate} budget={teaBudgetTiles.toDate} />
                    </div>
                  </div>
                )}
                <div className="sales-btile">
                  <div className="sales-btile-label">Full-year budget</div>
                  <div className="sales-btile-val">{money(teaBudgetTiles.fullYear, roundUp)}</div>
                  <div className="sales-btile-sub">
                    actual {money(netByCat["Tea Room"].total, roundUp)}{" "}
                    <Variance actual={netByCat["Tea Room"].total} budget={teaBudgetTiles.fullYear} />
                  </div>
                </div>
                {/* Today per tea room (2026-09-25): the rows dated today — live after a
                    "Refresh today", the overnight run's before one. Shown measure. */}
                <div className="sales-btile sales-btile--today" title={rs?.today_as_of ? `Live from MERAC as of ${prettyDateTime(rs.today_as_of)}` : "As at the overnight run"}>
                  <div className="sales-btile-label">Today's sales ({measure}){rs?.today_as_of ? ` · as of ${prettyDateTime(rs.today_as_of).replace(/^.*, /, "")}` : ""}</div>
                  <table className="sales-today-table">
                    <tbody>
                      {locationsByCat["Tea Room"].map((loc) => (
                        <tr key={loc}>
                          <td>{loc}</td>
                          <td className="ta-right">{todayByLocation.has(loc) ? money(todayByLocation.get(loc)!, roundUp) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>Tea Rooms today</td>
                        <td className="ta-right">{money(locationsByCat["Tea Room"].reduce((a, loc) => a + (todayByLocation.get(loc) ?? 0), 0), roundUp)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          )}
          <CategoryTable
            title="Tea Room Daily Sales"
            category="Tea Room"
            matrix={teaMatrix}
            allLocations={locationsByCat["Tea Room"]}
            hidden={hidden["Tea Room"]}
            onToggle={(loc) => toggleLoc("Tea Room", loc)}
            dayGrain={dayGrain}
            rangeLabel={rangeLabel}
            fmt={(n) => money(n, roundUp)}
            budget={teaPeriodBudget}
            breakdownMode={breakdownMode}
            onBreakdown={openBreakdown}
            classPills={{ hidden: classHidden["Tea Room"], onToggle: (c) => toggleClass("Tea Room", c) }}
            ly={lyTeaMatrix}
            lyLabel={lyData?.fiscalYearLabel ?? ""}
            showWalkUp={basis === "dept"}
          />
          {source === "tanc" && measure === "Net" && adjPeriod.cateringTotal !== 0 && (() => {
            const tanc = teaMatrix.grandTotal;                                       // MERAC catering nominal ledger (MStore 63*)
            const notMeracCatering = adjPeriod.cateringTotal - adjPeriod.meracCatering; // Trolley / Beerfest / Group catering
            const imbalance = adjPeriod.meracCatering - tanc;                        // = cateringTotal − tanc − notMeracCatering
            return (
              <div className="sales-pnl-recon">
                <div className="sales-pnl-recon-head">Reconciliation to iplicit P&amp;L · {rangeLabel}</div>
                <div className="sales-pnl-row">
                  <span className="sales-pnl-k">
                    Merac catering sales (TANC) <span className="sales-pnl-iplicit">(iplicit {money(adjPeriod.meracCatering, roundUp)})</span>
                  </span>
                  <span className="sales-pnl-v">{money(tanc, roundUp)}</span>
                </div>
                {notMeracCatering !== 0 && (
                  <div className="sales-pnl-row">
                    <span className="sales-pnl-k">+ Catering not through Merac (trolley, events…)</span>
                    <span className="sales-pnl-v">{money(notMeracCatering, roundUp)}</span>
                  </div>
                )}
                <div className="sales-pnl-row">
                  <span className="sales-pnl-k">Imbalance (overs/unders &amp; timing)</span>
                  <span className="sales-pnl-v">{money(imbalance, roundUp)}</span>
                </div>
                <div className="sales-pnl-row sales-pnl-total">
                  <span className="sales-pnl-k">P&amp;L total — matches iplicit</span>
                  <span className="sales-pnl-v">{money(adjPeriod.cateringTotal, roundUp)}</span>
                </div>
                <p className="sales-pnl-note">
                  Variances are overs &amp; unders and timing differences. iplicit figures are locked;
                  MERAC data is still subject to ad-hoc administrative changes.
                </p>
              </div>
            );
          })()}
          <MoversSection from={effRange.from} to={effRange.to} roundUp={roundUp} rangeLabel={rangeLabel} />
        </>
      )}

      {bd && (
        <BreakdownModal bd={bd} roundUp={roundUp} measureLabel={measure} onClose={() => setBd(null)} />
      )}
      {gcOverviewOpen && (
        <GiftCardOverviewModal fiscalYear={fiscalYear} roundUp={roundUp} onClose={() => setGcOverviewOpen(false)} />
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Small % variance chip (actual vs budget). Budget is net.
// ---------------------------------------------------------------------------
function Variance({ actual, budget }: { actual: number; budget: number }) {
  const pct = variancePct(actual, budget);
  if (pct == null) return null;
  const cls = pct >= 0 ? "up" : "down";
  return <span className={`sales-var ${cls}`}>{pct >= 0 ? "+" : ""}{pct.toFixed(1)}%</span>;
}

// ---------------------------------------------------------------------------
// One category section: location pills (+ optional class pills) + matrix table
// ---------------------------------------------------------------------------
interface Matrix {
  visible: string[];
  grid: { key: string; label: string; cells: number[]; rowTotal: number }[];
  colTotals: number[];
  grandTotal: number;
  netTotal: number;
  tradingDays: number;   // days in the period with any sale in this category
  walkUpCols: number[];  // dept basis: walk-up (ticket / car-park) share per visible location
  walkUpTotal: number;
}
/** Last season's column total for a location (0 when it had none). */
function lyColTotal(ly: Matrix, loc: string): number {
  const i = ly.visible.indexOf(loc);
  return i >= 0 ? ly.colTotals[i] : 0;
}
function perDay(total: number, days: number): number | null {
  return days > 0 ? total / days : null;
}

function CategoryTable(props: {
  title: string;
  category: Category;
  matrix: Matrix;
  allLocations: string[];
  hidden: Set<string>;
  onToggle: (loc: string) => void;
  dayGrain: boolean;
  rangeLabel: string;
  fmt: (n: number) => string;
  budget: number | null;
  breakdownMode: boolean;
  onBreakdown: (category: Category, locations: string, from: string, to: string, cls: string, title: string) => void;
  classPills?: { hidden: Set<SaleClass>; onToggle: (c: SaleClass) => void };
  /** Compare against season: last season's matrix built by the same code, or null. */
  ly?: Matrix | null;
  lyLabel?: string;
  /** Kathryn's Variant 1: show the "of which walk-up tickets" footer row. */
  showWalkUp?: boolean;
}) {
  const { title, category, matrix, allLocations, hidden, onToggle, dayGrain, rangeLabel, fmt, budget, breakdownMode, onBreakdown, classPills, ly, lyLabel, showWalkUp } = props;
  const lyRowTotals = new Map((ly?.grid ?? []).map((g) => [g.key, g.rowTotal]));
  const avg = perDay(matrix.grandTotal, matrix.tradingDays);
  const lyAvg = ly ? perDay(ly.grandTotal, ly.tradingDays) : null;

  // Active class filter to pass to the breakdown endpoint (only when exactly one is on).
  const activeClass = (): string => {
    if (!classPills) return "";
    const on = CLASSES.filter((c) => !classPills.hidden.has(c));
    return on.length === 1 ? on[0] : "";
  };
  const bucketRange = (bucketKey: string | null): { from: string; to: string } => {
    if (bucketKey) return dayGrain ? { from: bucketKey, to: bucketKey } : { from: `${bucketKey}-01`, to: lastDayIso(bucketKey) };
    const first = matrix.grid[0]?.key;
    const last = matrix.grid[matrix.grid.length - 1]?.key;
    if (!first || !last) return { from: "1900-01-01", to: "1900-01-01" };
    return dayGrain ? { from: first, to: last } : { from: `${first}-01`, to: lastDayIso(last) };
  };
  const clickCell = (locations: string, bucketKey: string | null, label: string) => {
    if (!breakdownMode) return;
    const { from, to } = bucketRange(bucketKey);
    onBreakdown(category, locations, from, to, activeClass(), `${category} · ${label}`);
  };

  // Budget has no per-location/class split, so only show the variance % when the
  // full set is shown — otherwise a filtered actual vs the whole budget misleads.
  const filtered = hidden.size > 0 || (classPills != null && classPills.hidden.size > 0);
  const netVar = budget != null && !filtered ? variancePct(matrix.netTotal, budget) : null;

  return (
    <section className="sales-section">
      <div className="sales-section-head">
        <h2>{title}</h2>
        <span className="sales-section-sub">
          {rangeLabel} · {fmt(matrix.grandTotal)}
          {matrix.tradingDays > 0 && (
            <span title="Days in the period with a sale in this table (Easter and the timetable move, so compare the per-day figure too)">
              {" · "}{matrix.tradingDays} trading day{matrix.tradingDays === 1 ? "" : "s"}{avg != null && <> · {fmt(avg)}/day</>}
            </span>
          )}
          {showWalkUp && matrix.walkUpTotal !== 0 && (
            <span className="sales-walkup-chip" title="Ticket, car-park and booking lines rung through these tills — included in the total (Kathryn's Variant 1)">
              of which walk-up tickets {fmt(matrix.walkUpTotal)}
            </span>
          )}
          {budget != null && !filtered && (
            <>
              {" · budget "}{fmt(budget)}
              {netVar != null && <span className={`sales-var ${netVar >= 0 ? "up" : "down"}`}> {netVar >= 0 ? "+" : ""}{netVar.toFixed(1)}%</span>}
            </>
          )}
          {ly && (
            <span className="sales-section-ly" style={{ color: LY_GREY }} title={`Last season ${lyLabel ?? ""}, same period`}>
              {" · last season "}<LyCell current={matrix.grandTotal} ly={ly.grandTotal} fmt={fmt} />
              {ly.tradingDays > 0 && <> · {ly.tradingDays} day{ly.tradingDays === 1 ? "" : "s"}{lyAvg != null && <> · {fmt(lyAvg)}/day</>}</>}
            </span>
          )}
        </span>
      </div>

      <div className="sales-pillrows">
        {allLocations.length > 0 && (
          <div className="sales-pills" role="group" aria-label={`${title} locations`}>
            {allLocations.map((loc) => (
              <button key={loc} type="button" className={"sales-pill" + (hidden.has(loc) ? " off" : "")} onClick={() => onToggle(loc)} aria-pressed={!hidden.has(loc)}>
                {loc}
              </button>
            ))}
          </div>
        )}
        {classPills && (
          <div className="sales-pills sales-classpills" role="group" aria-label={`${title} type`}>
            {CLASSES.map((c) => (
              <button key={c} type="button" className={"sales-pill sales-classpill" + (classPills.hidden.has(c) ? " off" : "")} onClick={() => classPills.onToggle(c)} aria-pressed={!classPills.hidden.has(c)}>
                {c === "Catering" ? "Catering" : "Retail"}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="sales-table-wrap">
        {matrix.visible.length === 0 ? (
          <div className="sales-empty">All locations hidden — tap a pill to show one.</div>
        ) : matrix.grid.length === 0 ? (
          <div className="sales-empty">No {title.toLowerCase()} in this period.</div>
        ) : (
          <table className={"sales-table" + (breakdownMode ? " breakdown" : "")}>
            <thead>
              <tr>
                <th className="sticky-col">{dayGrain ? "Date" : "Month"}</th>
                {matrix.visible.map((loc) => <th key={loc} className="ta-right">{loc}</th>)}
                <th className="ta-right sales-total-col">Total</th>
                {ly && <th className="ta-right sales-ly-col" title={`Last season ${lyLabel ?? ""} — the same dates (or weeks) a year earlier`}>Last season</th>}
              </tr>
            </thead>
            <tbody>
              {matrix.grid.map((g) => (
                <tr key={g.key}>
                  <td className="sticky-col">{g.label}</td>
                  {g.cells.map((v, i) => (
                    <td
                      key={i}
                      className={"ta-right" + (breakdownMode ? " clickable" : "")}
                      onClick={breakdownMode ? () => clickCell(matrix.visible[i], g.key, `${matrix.visible[i]} · ${g.label}`) : undefined}
                    >
                      {v ? fmt(v) : "–"}
                    </td>
                  ))}
                  <td
                    className={"ta-right sales-total-col" + (breakdownMode ? " clickable" : "")}
                    onClick={breakdownMode ? () => clickCell(matrix.visible.join("|"), g.key, `All locations · ${g.label}`) : undefined}
                  >
                    {fmt(g.rowTotal)}
                  </td>
                  {ly && <td className="ta-right sales-ly-col"><LyCell current={g.rowTotal} ly={lyRowTotals.get(g.key) ?? 0} fmt={fmt} /></td>}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="sticky-col">Total</td>
                {matrix.colTotals.map((v, i) => (
                  <td
                    key={i}
                    className={"ta-right" + (breakdownMode ? " clickable" : "")}
                    onClick={breakdownMode ? () => clickCell(matrix.visible[i], null, `${matrix.visible[i]} · ${rangeLabel}`) : undefined}
                  >
                    {fmt(v)}
                  </td>
                ))}
                <td
                  className={"ta-right sales-total-col" + (breakdownMode ? " clickable" : "")}
                  onClick={breakdownMode ? () => clickCell(matrix.visible.join("|"), null, `All locations · ${rangeLabel}`) : undefined}
                >
                  {fmt(matrix.grandTotal)}
                </td>
                {ly && <td className="ta-right sales-ly-col"><LyCell current={matrix.grandTotal} ly={ly.grandTotal} fmt={fmt} /></td>}
              </tr>
              {showWalkUp && matrix.walkUpTotal !== 0 && (
                <tr className="sales-pctrow sales-walkuprow" title="Ticket, car-park and booking lines rung through these tills — part of the totals above (Kathryn's Variant 1)">
                  <td className="sticky-col">of which walk-up tickets</td>
                  {matrix.walkUpCols.map((v, i) => <td key={i} className="ta-right">{v ? fmt(v) : "–"}</td>)}
                  <td className="ta-right sales-total-col">{fmt(matrix.walkUpTotal)}</td>
                  {ly && <td className="ta-right sales-ly-col" style={{ color: LY_GREY }}>{ly.walkUpTotal ? fmt(ly.walkUpTotal) : "–"}</td>}
                </tr>
              )}
              {ly && (
                <tr className="sales-pctrow sales-lyrow" style={{ color: LY_GREY }}>
                  <td className="sticky-col">Last season{ly.tradingDays ? ` (${ly.tradingDays} day${ly.tradingDays === 1 ? "" : "s"})` : ""}</td>
                  {matrix.visible.map((loc, i) => <td key={i} className="ta-right"><LyCell current={matrix.colTotals[i]} ly={lyColTotal(ly, loc)} fmt={fmt} /></td>)}
                  <td className="ta-right sales-total-col"><LyCell current={matrix.grandTotal} ly={ly.grandTotal} fmt={fmt} /></td>
                  <td className="ta-right sales-ly-col">{lyAvg != null ? <>{fmt(lyAvg)}/day</> : "–"}</td>
                </tr>
              )}
              <tr className="sales-pctrow">
                <td className="sticky-col">% of total</td>
                {matrix.colTotals.map((v, i) => (
                  <td key={i} className="ta-right">
                    {matrix.grandTotal ? `${((v / matrix.grandTotal) * 100).toFixed(1)}%` : "–"}
                  </td>
                ))}
                <td className="ta-right sales-total-col">{matrix.grandTotal ? "100%" : "–"}</td>
                {ly && <td className="ta-right sales-ly-col">{avg != null ? <>{fmt(avg)}/day</> : "–"}</td>}
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Movers & Shakers — top-20 retail lines by revenue, with cost + margin (R1).
// Retail only (MERAC holds no cost for most tea-room lines). Margin is on Net
// and uses current average cost; gift cards/vouchers are excluded; zero-cost
// lines are flagged rather than shown as 100% margin.
// ---------------------------------------------------------------------------
function MoversSection({ from, to, roundUp, rangeLabel }: { from: string; to: string; roundUp: boolean; rangeLabel: string }) {
  const [data, setData] = useState<MoversResp | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!from || !to) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`/api/dashboards/sales/movers?from=${from}&to=${to}&limit=20`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          throw new Error(b.error || `Request failed (${res.status})`);
        }
        return res.json() as Promise<MoversResp>;
      })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [from, to]);

  return (
    <section className="sales-section">
      <div className="sales-section-head">
        <h2>Movers &amp; Shakers — top 20 retail lines</h2>
        <span className="sales-section-sub">
          {rangeLabel} · ranked by Net revenue · margin at current cost (retail only)
          {data?.coverage_pct != null && ` · ${data.coverage_pct}% of revenue has a cost on file`}
        </span>
      </div>
      <div className="sales-table-wrap">
        {loading ? (
          <div className="sales-empty">Loading…</div>
        ) : error ? (
          <div className="sales-empty sales-empty--error">{error}</div>
        ) : !data || data.rows.length === 0 ? (
          <div className="sales-empty">No retail product sales in this period (or the product cache hasn’t refreshed yet).</div>
        ) : (
          <table className="sales-table">
            <thead>
              <tr>
                <th className="sticky-col">#</th>
                <th>Item</th>
                <th>Sales element</th>
                <th className="ta-right">Qty</th>
                <th className="ta-right">Revenue</th>
                <th className="ta-right">Cost</th>
                <th className="ta-right">Margin</th>
                <th className="ta-right">Margin %</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={r.plu}>
                  <td className="sticky-col">{i + 1}</td>
                  <td>
                    {r.item}
                    {!r.has_cost && (
                      <span title="No cost on file — margin can’t be shown" style={{ color: "#a33", marginLeft: 6, fontSize: 11 }}>⚠ no cost</span>
                    )}
                  </td>
                  <td>{r.analysis ?? "—"}</td>
                  <td className="ta-right">{r.qty.toLocaleString("en-GB")}</td>
                  <td className="ta-right">{money(r.net, roundUp)}</td>
                  <td className="ta-right">{r.has_cost ? money(r.cost, roundUp) : "—"}</td>
                  <td className="ta-right">{r.has_cost ? money(r.margin, roundUp) : "—"}</td>
                  <td className="ta-right">{r.has_cost && r.margin_pct != null ? `${r.margin_pct.toFixed(1)}%` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Breakdown popup — the sales-element (Analysis) split behind a clicked figure.
// ---------------------------------------------------------------------------
function BreakdownModal(props: {
  bd: { title: string; loading: boolean; error: string | null; rows: BreakdownItem[]; total: number };
  roundUp: boolean;
  measureLabel: Measure;
  onClose: () => void;
}) {
  const { bd, roundUp, measureLabel, onClose } = props;
  const key = measureLabel === "Gross" ? "Gross" : "Net";
  return (
    <div className="sales-modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="sales-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sales-modal-head">
          <div>
            <div className="sales-modal-title">Breakdown — {bd.title}</div>
            <div className="sales-modal-sub">By sales element ({measureLabel}) · total {money(bd.total, roundUp)}</div>
          </div>
          <button type="button" className="sales-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="sales-modal-body">
          {bd.loading ? (
            <div className="sales-empty">Loading…</div>
          ) : bd.error ? (
            <div className="sales-empty sales-empty--error">{bd.error}</div>
          ) : bd.rows.length === 0 ? (
            <div className="sales-empty">No detail for this selection.</div>
          ) : (
            <table className="sales-bd-table">
              <thead>
                <tr><th>Sales element</th><th className="ta-right">{measureLabel}</th><th className="ta-right">Items</th><th className="ta-right">Share</th></tr>
              </thead>
              <tbody>
                {bd.rows.map((r) => {
                  const v = r[key];
                  const share = bd.total !== 0 ? (v / bd.total) * 100 : 0;
                  return (
                    <tr key={`${r.Analysis ?? "?"}|${r.WalkUp ? "w" : ""}`}>
                      <td>
                        {r.Analysis ?? "(unclassified)"}
                        {r.WalkUp && <span className="sales-bd-walkup" title="A ticket, car-park or booking line rung through the shop till — included under Kathryn's Variant 1 only">walk-up</span>}
                      </td>
                      <td className="ta-right">{money(v, roundUp)}</td>
                      <td className="ta-right">{r.Items}</td>
                      <td className="ta-right">{share.toFixed(1)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PDF export — landscape, NYMR-green header, both matrices via autotable.
// ---------------------------------------------------------------------------
interface ExportCtx {
  measure: Measure;
  retailMatrix: Matrix;
  teaMatrix: Matrix;
  rangeLabel: string;
  basis: Basis;
  lyRetail: Matrix | null;
  lyTea: Matrix | null;
  lyLabel: string | null;
}
function exportPdf(data: SalesResponse, ctx: ExportCtx & { roundUp: boolean }) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const printedOn = new Date().toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
  });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(11, 61, 46);
  doc.text(`Retail & Tea Room Sales — ${data.fiscalYearLabel}`, 10, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(90, 90, 90);
  doc.text(`${ctx.rangeLabel}  ·  ${ctx.measure} (${ctx.measure === "Net" ? "ex VAT" : "inc VAT"})  ·  ${BASIS_LABEL[ctx.basis]}${ctx.basis === "dept" ? " (department basis incl. walk-up tickets)" : " (sale-type basis)"}${ctx.lyLabel ? `  ·  vs ${ctx.lyLabel}` : ""}  ·  printed ${printedOn}`, 10, 17);

  let startY = 22;
  for (const [title, m, ly] of [["Retail Daily Sales", ctx.retailMatrix, ctx.lyRetail], ["Tea Room Daily Sales", ctx.teaMatrix, ctx.lyTea]] as [string, Matrix, Matrix | null][]) {
    if (m.visible.length === 0 || m.grid.length === 0) continue;
    const lyRow = new Map((ly?.grid ?? []).map((g) => [g.key, g.rowTotal]));
    const head = [["Date/Month", ...m.visible, "Total", ...(ly ? ["Last season"] : [])]];
    const body = m.grid.map((g) => [g.label, ...g.cells.map((v) => (v ? money(v, ctx.roundUp) : "")), money(g.rowTotal, ctx.roundUp), ...(ly ? [money(lyRow.get(g.key) ?? 0, ctx.roundUp)] : [])]);
    body.push(["Total", ...m.colTotals.map((v) => money(v, ctx.roundUp)), money(m.grandTotal, ctx.roundUp), ...(ly ? [money(ly.grandTotal, ctx.roundUp)] : [])]);
    if (ctx.basis === "dept" && m.walkUpTotal !== 0) body.push(["of which walk-up tickets", ...m.walkUpCols.map((v) => (v ? money(v, ctx.roundUp) : "")), money(m.walkUpTotal, ctx.roundUp), ...(ly ? [ly.walkUpTotal ? money(ly.walkUpTotal, ctx.roundUp) : ""] : [])]);
    if (ly) body.push([`Last season (${ly.tradingDays} days)`, ...m.visible.map((loc) => money(lyColTotal(ly, loc), ctx.roundUp)), money(ly.grandTotal, ctx.roundUp), ly.tradingDays ? `${money(ly.grandTotal / ly.tradingDays, ctx.roundUp)}/day` : ""]);
    body.push([`Trading days: ${m.tradingDays}`, ...m.visible.map(() => ""), m.tradingDays ? `${money(m.grandTotal / m.tradingDays, ctx.roundUp)}/day` : "", ...(ly ? [""] : [])]);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(11, 61, 46);
    doc.text(title, 10, startY);
    autoTable(doc, {
      head,
      body,
      startY: startY + 2,
      margin: { left: 8, right: 8 },
      styles: { fontSize: 7, cellPadding: 1.2, overflow: "linebreak", halign: "right" },
      columnStyles: { 0: { halign: "left" } },
      headStyles: { fillColor: [31, 77, 63], textColor: 255, fontStyle: "bold", halign: "right" },
      alternateRowStyles: { fillColor: [250, 248, 242] },
      footStyles: { fillColor: [230, 236, 232], textColor: 20, fontStyle: "bold" },
      didParseCell: (d) => {
        const label = String(d.row.raw && Array.isArray(d.row.raw) ? d.row.raw[0] : "");
        if (label === "Total") d.cell.styles.fontStyle = "bold";
        if (label.startsWith("Last season") || d.column.index === head[0].length - 1 && ly) d.cell.styles.textColor = [138, 133, 119];
      },
    });
    const finalY = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY;
    startY = (finalY ?? startY) + 10;
  }
  doc.save(`retail-tearoom-sales-${data.fiscalYearLabel.replace("/", "-")}-${ctx.rangeLabel.replace(/[^\w]+/g, "_")}.pdf`);
}

// ---------------------------------------------------------------------------
// Excel export — three sheets (Summary, Retail, Tea Room) as real .xlsx.
// ---------------------------------------------------------------------------
type XlsxCell = { value: string | number | null; type?: typeof String | typeof Number; fontWeight?: "bold"; format?: string; align?: "left" | "right" };
const CURRENCY_FMT = "£#,##0.00";

function matrixSheet(m: Matrix, bucketHeader: string, opts: { ly: Matrix | null; walkUp: boolean }): XlsxCell[][] {
  const { ly, walkUp } = opts;
  const lyRow = new Map((ly?.grid ?? []).map((g) => [g.key, g.rowTotal]));
  const cur = (v: number, bold = false): XlsxCell => ({ value: v, type: Number, format: CURRENCY_FMT, ...(bold ? { fontWeight: "bold" } : {}) });
  const header: XlsxCell[] = [
    { value: bucketHeader, type: String, fontWeight: "bold" },
    ...m.visible.map((loc) => ({ value: loc, type: String, fontWeight: "bold", align: "right" } as XlsxCell)),
    { value: "Total", type: String, fontWeight: "bold", align: "right" },
    ...(ly ? [{ value: "Last season", type: String, fontWeight: "bold", align: "right" } as XlsxCell] : []),
  ];
  const body: XlsxCell[][] = m.grid.map((g) => [
    { value: g.label, type: String },
    ...g.cells.map((v) => cur(v)),
    cur(g.rowTotal, true),
    ...(ly ? [cur(lyRow.get(g.key) ?? 0)] : []),
  ]);
  const footer: XlsxCell[][] = [[
    { value: "Total", type: String, fontWeight: "bold" },
    ...m.colTotals.map((v) => cur(v, true)),
    cur(m.grandTotal, true),
    ...(ly ? [cur(ly.grandTotal, true)] : []),
  ]];
  if (walkUp && m.walkUpTotal !== 0) {
    footer.push([
      { value: "of which walk-up tickets", type: String },
      ...m.walkUpCols.map((v) => cur(v)),
      cur(m.walkUpTotal),
      ...(ly ? [cur(ly.walkUpTotal)] : []),
    ]);
  }
  if (ly) {
    footer.push([
      { value: `Last season (${ly.tradingDays} trading days)`, type: String },
      ...m.visible.map((loc) => cur(lyColTotal(ly, loc))),
      cur(ly.grandTotal),
      { value: ly.tradingDays ? ly.grandTotal / ly.tradingDays : null, type: Number, format: CURRENCY_FMT },
    ]);
  }
  footer.push([
    { value: `Trading days: ${m.tradingDays}`, type: String },
    ...m.visible.map(() => ({ value: null } as XlsxCell)),
    { value: m.tradingDays ? m.grandTotal / m.tradingDays : null, type: Number, format: CURRENCY_FMT },
    ...(ly ? [{ value: "per trading day", type: String, align: "right" } as XlsxCell] : []),
  ]);
  return [header, ...body, ...footer];
}

async function exportExcel(
  data: SalesResponse,
  ctx: ExportCtx & { summary: { items: { location: string; value: number; walkUp: number }[]; total: number; walkUp: number }; lySummary: { items: { location: string; value: number }[]; total: number } | null }
) {
  const lyByLoc = new Map((ctx.lySummary?.items ?? []).map((it) => [it.location, it.value]));
  const dept = ctx.basis === "dept";
  const summarySheet: XlsxCell[][] = [
    [{ value: `Retail — season ${data.fiscalYearLabel} by location (${ctx.measure}) — ${BASIS_LABEL[ctx.basis]}${dept ? " (department basis incl. walk-up tickets)" : " (sale-type basis)"}`, type: String, fontWeight: "bold" }, { value: null }],
    [
      { value: "Location", type: String, fontWeight: "bold" },
      { value: ctx.measure, type: String, fontWeight: "bold", align: "right" },
      ...(dept ? [{ value: "of which walk-up", type: String, fontWeight: "bold", align: "right" } as XlsxCell] : []),
      ...(ctx.lySummary ? [{ value: ctx.lyLabel ?? "Last season", type: String, fontWeight: "bold", align: "right" } as XlsxCell] : []),
    ],
    ...ctx.summary.items.map((it) => [
      { value: it.location, type: String } as XlsxCell,
      { value: it.value, type: Number, format: CURRENCY_FMT } as XlsxCell,
      ...(dept ? [{ value: it.walkUp, type: Number, format: CURRENCY_FMT } as XlsxCell] : []),
      ...(ctx.lySummary ? [{ value: lyByLoc.get(it.location) ?? 0, type: Number, format: CURRENCY_FMT } as XlsxCell] : []),
    ]),
    [
      { value: "Total", type: String, fontWeight: "bold" },
      { value: ctx.summary.total, type: Number, format: CURRENCY_FMT, fontWeight: "bold" },
      ...(dept ? [{ value: ctx.summary.walkUp, type: Number, format: CURRENCY_FMT, fontWeight: "bold" } as XlsxCell] : []),
      ...(ctx.lySummary ? [{ value: ctx.lySummary.total, type: Number, format: CURRENCY_FMT, fontWeight: "bold" } as XlsxCell] : []),
    ],
  ];
  const sheets = [
    { data: summarySheet, sheet: "Summary" },
    { data: matrixSheet(ctx.retailMatrix, "Date/Month", { ly: ctx.lyRetail, walkUp: dept }), sheet: "Retail" },
    { data: matrixSheet(ctx.teaMatrix, "Date/Month", { ly: ctx.lyTea, walkUp: dept }), sheet: "Tea Room" },
  ];
  const fileName = `retail-tearoom-sales-${data.fiscalYearLabel.replace("/", "-")}-${ctx.rangeLabel.replace(/[^\w]+/g, "_")}.xlsx`;
  const blob = await writeXlsxFile(sheets as never).toBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
